import { yupResolver } from "@hookform/resolvers/yup";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import { getDataFromResponse } from "~/api/axiosUtils.ts";
import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint";
import FormSection, { FormSectionGroup } from "~/components/formElements/FormSection";
import Input from "~/components/formElements/Input";
import SubmitButton from "~/components/formElements/SubmitButton";
import AccountingDetailsFormSection from "~/components/formSections/AccountingDetailsFormSection";
import AddressFormSection from "~/components/formSections/AddressFormSection/AddressFormSection.tsx";
import CommentFormSection from "~/components/formSections/CommentFormSection";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarContent from "~/components/Sidebar/components/SidebarContent";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import { DEFAULT_COUNTRY_CODE } from "~/constants/form.ts";
import useCountryCodeSelectOptions from "~/hooks/form/formOptionsData/useCountryCodeSelectOptions.ts";
import { useFormIsSubmittable } from "~/hooks/form/useFormIsSubmittable.ts";
import {
	useClientValidationComparisonData,
} from "~/hooks/form/validationComparisonData/useClientValidationComparisonData.ts";
import { createClient } from "~/modules/client/api/client/clientApiDispatchers.ts";
import { useAllClients } from "~/modules/client/api/client/clientQueries.ts";
import type { CreateClientData } from "~/modules/client/api/client/clientTypes.ts";
import { useAllCompanies } from "~/modules/client/api/company/companyQueries.ts";
import { preventSubmitOnEnter } from "~/utils/form/formUtils.ts";
import { byObjectProperty } from "~/utils/sortFunctions.ts";

type Props = {
	onSuccess: (newCompanyId: string) => void;
	companyId: string;
};

interface CreateClientFormData extends Omit<CreateClientData, "paymentTargetDays" | "companyId" | "address"> {
	addressLine1: string | null;
	addressLine2: string | null;
	addressLine3: string | null;
	city: string | null;
	postCode: string | null;
	countryCode: string | null;
	paymentTargetDays: number;
	paymentTargetDaysCustom?: number | null;
}

const CreateClientForm: React.FC<Props> = ({ onSuccess, companyId }) => {
	const [busy, setBusy] = useState(false);

	const { data: allCompaniesData } = useAllCompanies();
	const { data: allClientsData } = useAllClients();

	const companyData = useMemo(() => {
		if (allCompaniesData) {
			return allCompaniesData.find((company) => company.id === companyId);
		}
	}, [allCompaniesData, companyId]);

	const clientAddressData = useMemo(() => {
		if (allClientsData) {
			const clientData = allClientsData.filter((client) => client.companyId === companyId).sort(byObjectProperty("id"));
			if (clientData.length > 0) {
				return clientData[0];
			}
		}
		return null;
	}, [allClientsData, companyId]);

	const displayNameComparisonData = useClientValidationComparisonData();

	const schema = useMemo(() => {
		return yup.object({
			addressLine1: yup.string().default(null),
			addressLine2: yup.string().default(null),
			addressLine3: yup.string().default(null),
			city: yup.string().default(null),
			comment: yup.string().default(null),
			countryCode: yup.string().default(null),
			displayName: yup.string().required().unique(displayNameComparisonData),
			invoiceByMail: yup.boolean().required(),
			isVatExempt: yup.boolean().required(),
			paymentTargetDays: yup.number().required(),
			paymentTargetDaysCustom: yup.number()
				.nullable()
				.when("paymentTargetDays", (field1Value: number[], schema) => {
					return field1Value[0] === 0
						? schema.required()
						: schema.nullable();
				}),
			postCode: yup.string().default(null),
			travelExpensesIncluded: yup.boolean().required(),
		});
	}, [displayNameComparisonData]);

	const siblingAddress = clientAddressData?.address;

	const defaultValues = useMemo(() => {
		return {
			addressLine1: siblingAddress?.addressLine1 || "",
			addressLine2: siblingAddress?.addressLine2 || "",
			addressLine3: siblingAddress?.addressLine3 || "",
			city: siblingAddress?.city || "",
			comment: "",
			countryCode: siblingAddress?.countryCode || DEFAULT_COUNTRY_CODE,
			displayName: "",
			invoiceByMail: true,
			isVatExempt: false,
			paymentTargetDays: 30,
			paymentTargetDaysCustom: null,
			postCode: siblingAddress?.postCode || "",
			travelExpensesIncluded: true,
		};
	}, [siblingAddress]);

	const {
		handleSubmit,
		control,
		watch,
		formState: { isValid, isDirty, isSubmitted },
		trigger,
	} = useForm<CreateClientFormData>({
		defaultValues,
		resolver: yupResolver<CreateClientFormData>(schema),
	});

	const formIsSubmittable = useFormIsSubmittable({ isDirty, isSubmitted, isValid, isLoading: busy });

	const showCustomPaymentTargetInput = watch("paymentTargetDays") === 0;

	const countryCodeSelectOptions = useCountryCodeSelectOptions();

	const onSubmit: SubmitHandler<CreateClientFormData> = async (data) => {
		setBusy(true);
		const { paymentTargetDays, paymentTargetDaysCustom, addressLine1, addressLine2, addressLine3, city, postCode, countryCode, ...rest } = data;
		const paymentTargetDaysValue =
			paymentTargetDays === 0 && paymentTargetDaysCustom ? paymentTargetDaysCustom : paymentTargetDays;

		const hasAddress = addressLine1 || addressLine2 || addressLine3 || city || postCode || countryCode;
		const address = hasAddress ? { addressLine1, addressLine2, addressLine3, city, postCode, countryCode } : null;

		const newClientData: CreateClientData = { paymentTargetDays: paymentTargetDaysValue, companyId, address, ...rest };

		try {
			const newClientResponse = await createClient(newClientData);
			const clientId = getDataFromResponse(newClientResponse).id;
			onSuccess(clientId);
		} catch (error) {
			console.log(error);
		}
	};

	useEffect(() => {
		if (showCustomPaymentTargetInput) {
			trigger("paymentTargetDaysCustom");
		}
	}, [showCustomPaymentTargetInput, trigger]);

	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			onKeyDown={preventSubmitOnEnter}
			className="flex min-h-full w-full flex-col justify-start"
		>
			<SidebarHeader>
				<SidebarHeaderHeadline>Neue Firma für Kunden &quot;{companyData?.displayName}&quot; anlegen</SidebarHeaderHeadline>
			</SidebarHeader>
			<SidebarContent>
				{busy && <SidebarBusyOverlay />}
				<FormSectionGroup>
					<FormSection title="Firma">
						<div className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-6">
							<div className="col-span-full sm:col-start-1">
								<Input
									name="displayName"
									label="Name Tochterfirma"
									control={control}
									placeholder="Name der Legaleinheit - bspw. Muster GmbH" />
							</div>
						</div>
					</FormSection>

					<AddressFormSection<CreateClientFormData>
						control={control}
						countryCodeSelectOptions={countryCodeSelectOptions}
						initialShowLine3={!!siblingAddress?.addressLine3}
					/>

					<AccountingDetailsFormSection<CreateClientFormData>
						control={control}
						showCustomPaymentTargetInput={showCustomPaymentTargetInput}
					/>

					<CommentFormSection
						control={control}
						label="Kommentar"
						title="Kommentar"
						placeholder="Hier können allgemeine Anmerkungen für die Rechnungserstellung hinterlegt werden."
					/>
				</FormSectionGroup>
			</SidebarContent>
			<SidebarFooter>
				<FormHasErrorsHint show={isSubmitted && !isValid}
								   className="mr-2" />
				<SubmitButton busy={busy}
							  disabled={!formIsSubmittable}>
					Speichern
				</SubmitButton>
			</SidebarFooter>
		</form>
	);
};

export default CreateClientForm;
